<template>
  <refonte-modal
    v-bind:show="props.show_modal"
    v-on:close="props.onclose"
    v-bind:title="
      'Signer électroniquement votre ' +
      (props.contrat?.typeContrat === 'Contrat' ? 'contrat' : 'avenant')
    "
    class="!max-w-[720px]">
    <template v-slot:modalBody>
      <div class="flex flex-col gap-[16px] pb-[12px]">
        <div
          class="mb-2 flex items-center gap-2 rounded border border-blue-100/50 bg-refonte-pale-violet-2 px-4 py-3 text-sm text-blue-800">
          <span class="info-icon shrink-0 text-blue-600">
            <IconInformation />
          </span>
          <div>
            Vous recevrez d'abord un
            <strong>email contenant un lien sécurisé</strong>, puis un
            <strong>code SMS</strong>
            unique sera nécessaire pour authentifier sa signature et valider le
            document.
          </div>
        </div>
        <div
          class="flex w-full items-start gap-3 rounded-lg border border-blue-100 bg-refonte-pale-violet-2 p-4 text-left text-sm">
          <div class="w-full">
            <div class="mb-0.5 w-full font-bold tracking-wide text-blue-600">
              Vérifiez vos informations
            </div>
            <div class="font-bold">
              {{ props.formateur.nom }} {{ props.formateur.prenom }}
            </div>
            <div>{{ props.formateur.email }}</div>
            <div class="italic">
              {{ props.formateur.telephone }}
            </div>
            <div
              class="text-xs font-bold italic text-jinius-red"
              v-if="!props.formateur.telephone">
              Un numéro de téléphone mobile est requis pour recevoir votre code
              SMS de validation.
            </div>
            <div
              class="text-xs font-bold italic text-jinius-red"
              v-if="
                props.formateur.telephone &&
                !isPhoneValid(props.formateur.telephone)
              ">
              Le numéro renseigné n'est pas un numéro de téléphone mobile
              international valide (ex. : +33 6 12 34 56 78).
            </div>
            <hr class="mx-auto my-4 w-full border-core-300" />
            <div class="mb-2 flex items-center gap-2 text-xs">
              <span class="info-icon shrink-0 text-blue-600">
                <IconModify />
              </span>
              <div>
                Pour
                {{ props.formateur.telephone ? 'modifier' : 'ajouter' }} votre
                numéro de téléphone, rendez-vous sur la page
                <span
                  class="cursor-pointer font-bold text-blue-600 hover:underline"
                  v-on:click="goToInformationPersonnelles"
                  >Mes informations personnelles.</span
                >
                <br />
                Pour corriger votre nom, prénom, ou email, rendez-vous sur la
                page
                <span
                  class="cursor-pointer font-bold text-blue-600 hover:underline"
                  v-on:click="goToAccount"
                  >Mon compte.</span
                >
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>

    <template v-slot:modalFooter>
      <refonte-button
        variant="secondary"
        v-bind:on_click="props.cancel">
        Annuler
      </refonte-button>
      <refonte-button
        v-bind:disabled="
          !isPhoneValid(props.formateur.telephone) || buttonDisabled
        "
        v-bind:on_click="onSubmit">
        Lancer la procédure
      </refonte-button>
    </template>
  </refonte-modal>
</template>

<script setup lang="ts">
  import IconInformation from '@/assets/img/refonte/icons/information/icon-information.svg?component';
  import RefonteModal from '@/components/refonte/modals/refonte-modal.vue';
  import IconModify from '@/assets/img/refonte/icons/action/icon-modify.svg?component';
  import RefonteButton from '@/components/refonte/buttons/refonte-button.vue';
  import { isPhoneValid } from '@/mixin/utils/phone_utils';
  import { FormateurContrat } from '@/types/formateur/formateur-contrat';
  import { InfoFormateurSignature } from '@/types/formateur/info-formateur-signature';
  import { ref } from 'vue';

  const props = withDefaults(
    defineProps<{
      class?: string;
      show_modal: boolean;
      contrat?: FormateurContrat;
      onclose: Function;
      formateur: InfoFormateurSignature;
      cancel?: Function;
      submit?: Function;
    }>(),
    {
      class: ''
    }
  );

  const buttonDisabled = ref<boolean>(false);
  const onSubmit = () => {
    buttonDisabled.value = true;
    if (props.submit) {
      props.submit();
    }
  };
  const goToInformationPersonnelles = () => {
    window.open('/account/informations#telephone-mobile', '_blank');
  };

  const goToAccount = () => {
    window.open(`${import.meta.env.VITE_API_AUTH}/account/redirect`, '_blank');
  };
</script>

<style lang="scss" scoped>
  .subtitle {
    @apply flex grow flex-wrap items-center self-stretch;
    gap: 8px;

    .subtitle-text {
      font-size: 13px;
      font-weight: 400;
      line-height: 16px;
    }

    .subtitle-icon-date {
      @apply flex items-center justify-center;
      height: 16px;

      :deep(svg) {
        @apply text-refonte_blue-200;
        width: 16px;
        height: 16px;
      }
    }
  }

  .info-icon {
    @apply mr-2 flex shrink-0 items-center justify-center;
    width: 16px;
    height: 16px;

    :deep(svg) {
      @apply text-refonte_blue-600;
      width: 16px;
      height: 16px;
    }
  }

  .info-add-participant-icon {
    @apply flex shrink-0 items-center justify-center;
    width: 14px;
    height: 14px;

    :deep(svg) {
      @apply text-refonte_blue-600;
      width: 14px;
      height: 14px;
    }
  }

  .description-card {
    @apply bg-refonte_blue-100 text-refonte_blue-600;
    .description-card-title {
      font-weight: 600;
      font-size: 14px;
    }
  }

  .selected-card {
    @apply bg-refonte-pale-violet-2 text-refonte-black;
    gap: 6px;
    line-height: 20px;

    .selected-card-title {
      font-weight: 600;
      font-size: 14px;
    }
  }

  .warning-card {
    @apply flex border-amber-100 bg-amber-50;
    .warning-card-row {
      @apply flex items-center;
      gap: 6px;
    }
  }

  .icon-phone {
    @apply flex shrink-0 items-center justify-center;
    width: 14px;
    height: 14px;

    :deep(svg) {
      @apply text-refonte_blue-600;
      width: 14px;
      height: 14px;
    }
  }
</style>
